import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type * as React from 'react';
import type { NotificationGroup, NotificationProps } from '@/shared/types/message';
import { notificationService } from '../services/NotificationService';
import { STACK_LAYOUT_SETTLE_MS } from '../components/molecules/notificationItemStackLayout';
/**
 * Estado, temporizadores y manejadores del mazo apilado de notificaciones (`NotificationItem`).
 *
 * Encapsula visibilidad tras `isLeaving`, expansión por hover, lista completa, asentamiento de layout
 * tras mutar la cola y supresión de `mouseleave` espurios al cerrar o al abrir la lista.
 *
 * @param group - Grupo de notificación del servicio (cola, contador, color, `isLeaving`).
 * @returns Objeto con:
 * - `deckHoverRef` — contenedor del mazo (padding inferior + hit area).
 * - `isVisible` — oculta el mazo tras animación de salida cuando `isLeaving` es true.
 * - `isStackExpanded` — hover: muestra todas las capas de la cola en vista previa.
 * - `isFullListOpen` — lista vertical completa tras hover + clic.
 * - `listMotionEnter` — pulso de entrada de la lista extendida (opacidad / translate).
 * - `queueLayoutSettling` — ventana sin transición de padding tras cambiar la cola.
 * - `showTimer` y `notificationDuration` — lectura actual de `notificationService.getConfig()` (timer global).
 * - `handleDismissOne` — cierre: la más reciente vacía el mazo (`dismissStack`); el resto `dismiss(id)` (callbacks vía servicio).
 * - `handleStackOpenListCapture` — captura de clic para abrir lista completa (sin interactivos).
 * - `handleDeckMouseLeave` / `handleDeckMouseEnter` — hover del contenedor del mazo.
 * - `hideStackHoverChrome` — atenuar pie y barra en stack apilado con hover (no en lista completa).
 */
export function useNotificationItemDeck(group: NotificationGroup) {
  const { queue, count, isLeaving, stackKey } = group;
  const queueIdSignature =
    queue.length > 0 ? queue.map((n) => (n.id !== undefined ? String(n.id) : '')).join(':') : '';

  const [isVisible, setIsVisible] = useState(true);
  const [isStackExpanded, setIsStackExpanded] = useState(false);
  /** Vista expandida: todas las notificaciones en columna (solo tras hover y clic). */
  const [isFullListOpen, setIsFullListOpen] = useState(false);
  /** Entrada suave de la lista extendida (opacidad + ligero desplazamiento). */
  const [listMotionEnter, setListMotionEnter] = useState(false);
  /** Tras mutar la cola: sin transiciones de layout unos ms (evita fantasma con clics rápidos). */
  const [queueLayoutSettling, setQueueLayoutSettling] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** Contenedor del mazo + padding inferior reservado (hit-testing al asomar capas por debajo). */
  const deckHoverRef = useRef<HTMLDivElement>(null);
  /**
   * Al pasar de stack a lista, el nodo bajo el puntero se desmonta y el navegador puede emitir
   * `mouseleave` en el contenedor; ignoramos ese leave hasta después del repintado.
   */
  const suppressDeckLeaveRef = useRef(false);
  const queueSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextQueueSettleRef = useRef(true);

  const { showTimer, duration: notificationDuration } = notificationService.getConfig();

  useLayoutEffect(() => {
    if (skipNextQueueSettleRef.current) {
      skipNextQueueSettleRef.current = false;
      return;
    }
    setQueueLayoutSettling(true);
    if (queueSettleTimerRef.current) clearTimeout(queueSettleTimerRef.current);
    queueSettleTimerRef.current = setTimeout(() => {
      setQueueLayoutSettling(false);
      queueSettleTimerRef.current = null;
    }, STACK_LAYOUT_SETTLE_MS);
    return () => {
      if (queueSettleTimerRef.current) {
        clearTimeout(queueSettleTimerRef.current);
        queueSettleTimerRef.current = null;
      }
    };
  }, [queueIdSignature]);

  useEffect(() => {
    if (isLeaving) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 300);
    } else {
      // Recuperar visibilidad al salir de dismissGroup o al mutar la cola sin isLeaving (p. ej. timer).
      setIsVisible(true);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLeaving, queue.length]);

  useEffect(() => {
    if (queue.length < 2) setIsFullListOpen(false);
  }, [queue.length]);

  useEffect(() => {
    if (!isFullListOpen) {
      setListMotionEnter(false);
      return;
    }
    setListMotionEnter(false);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setListMotionEnter(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [isFullListOpen]);

  useLayoutEffect(() => {
    if (!isFullListOpen) return;
    let alive = true;
    let innerId = 0;
    const outerId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => {
        if (alive) suppressDeckLeaveRef.current = false;
      });
    });
    return () => {
      alive = false;
      cancelAnimationFrame(outerId);
      if (innerId) cancelAnimationFrame(innerId);
      suppressDeckLeaveRef.current = false;
    };
  }, [isFullListOpen]);

  const handleDismissOne = (n: NotificationProps) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (n.id === undefined) return;
    // Al cerrar, el DOM puede emitir mouseleave sobre el mazo sin que el usuario haya “salido” de verdad;
    // evita colapsar el stack extendido por hover (misma idea que al abrir la lista completa).
    suppressDeckLeaveRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        suppressDeckLeaveRef.current = false;
      });
    });
    const newestId = queue[queue.length - 1]?.id;
    if (n.id === newestId) {
      notificationService.dismissStack(stackKey);
      return;
    }
    notificationService.dismiss(n.id);
  };

  const handleStackOpenListCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isStackExpanded || isFullListOpen || queue.length < 2) return;
    const el = e.target;
    if (!(el instanceof Element)) return;
    if (el.closest('button, a, input, textarea, select')) return;
    e.stopPropagation();
    suppressDeckLeaveRef.current = true;
    setIsFullListOpen(true);
  };

  const handleDeckMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (suppressDeckLeaveRef.current) return;
    const related = e.relatedTarget;
    if (related instanceof Node && deckHoverRef.current?.contains(related)) return;
    setIsStackExpanded(false);
    // En modo extendido (lista completa), el hover out no cierra la vista extendida.
  };

  const handleDeckMouseEnter = () => {
    setIsStackExpanded(true);
  };

  /** Hover en el mazo (solo vista apilada): atenuar pie y barra sin pausar el timer. Con lista completa abierta, no. */
  const hideStackHoverChrome = isStackExpanded && count > 1 && !isFullListOpen;

  return {
    deckHoverRef,
    isVisible,
    isStackExpanded,
    isFullListOpen,
    listMotionEnter,
    queueLayoutSettling,
    showTimer,
    notificationDuration,
    handleDismissOne,
    handleStackOpenListCapture,
    handleDeckMouseLeave,
    handleDeckMouseEnter,
    hideStackHoverChrome,
  };
}
